import { inject, injectable, injectFromBase } from 'inversify'
import ExternalTrackerService from '@/services/tracking/externalTrackerService'
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { Plugin } from '@/composition/vue/compositionApi'
import type { ExternalTrackerAsPlugin, TrackableConfig } from '@/services/tracking/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type CookieConsentService from '@/services/browser/cookieConsentService'
import type SubscriptionService from '@/services/transport/subscriptionService'
import { ROUTER_AFTER_EACH } from '@/services/route/types'
import { isProd } from '@/utils/system'

/**
 * https://developers.openai.com/ads/measurement-pixel
 */
@injectable()
@injectFromBase()
export class OpenAiPixelService extends ExternalTrackerService {
  protected trackingId = 'P4mw8TexLvvPVDSBaMFKj7'

  protected isSdkRequested = false

  protected appliedConsent: boolean | undefined = undefined

  constructor(
    @inject(SERVICE_TYPES.CookieConsentService) protected readonly cookieConsentService: CookieConsentService,
    @inject(SERVICE_TYPES.SubscriptionService) protected readonly subscriptionService: SubscriptionService,
  ) {
    super()
  }

  public getConfig(): TrackableConfig {
    return {
      id: this.trackingId,
    }
  }

  public getPlugin(): Plugin {
    return {
      install: () => this.initialize(),
    } satisfies Plugin
  }

  protected async initialize(): Promise<void> {
    const subscriptionKey = 'openAiPixelConsent'

    this.syncConsent()

    if (this.subscriptionService.exist(subscriptionKey)) {
      return
    }

    this.subscriptionService.subscribeWithSubscriptionKey(
      ROUTER_AFTER_EACH,
      () => this.syncConsent(),
      subscriptionKey,
      true,
    )
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  public override cleanUp(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler)
  }

  protected visibilityHandler = () => {
    if (!document.hidden) {
      this.syncConsent()
    }
  }

  protected syncConsent(): void {
    const hasConsent = this.cookieConsentService.hasAdvertisementConsent()

    if (hasConsent === this.appliedConsent) {
      return
    }

    this.appliedConsent = hasConsent

    if (!this.isSdkRequested) {
      if (hasConsent) {
        this.loadSdk()
      }
      return
    }

    window.oaiq?.('consent', hasConsent)
  }

  protected loadSdk(): void {
    this.isSdkRequested = true

    /* eslint-disable */
    /* prettier-ignore */
    const trackingHandler = async () => {
      // @ts-expect-error tracker initial script from docs
      !function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js")

      // Consent has to be queued ahead of init so the SDK never fires an ungranted request
      window.oaiq!('consent', true)
      window.oaiq!('init', { pixelId: this.trackingId, debug: !isProd })
    }
    /* eslint-enable */

    const trackerAsPlugin: ExternalTrackerAsPlugin = {
      trackingHandler,
      loadOnCustomEvent: true,
    }

    this.initializeTrackerAsPlugin(trackerAsPlugin)
  }
}
